import {
  Agreement01Icon,
  Analytics01Icon,
  Calendar03Icon,
  DashboardSquare01Icon,
  Settings02Icon,
  Mail01Icon,
  Notification01Icon,
  PresentationBarChart01Icon,
  QrCodeIcon,
  ThumbsUpIcon,
  RefreshIcon,
  UserGroupIcon,
  UserMultiple02Icon,
  WhatsappIcon,
} from '@hugeicons/core-free-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The Connection agent: Bridged's matching engine suggests who should meet at an event, the
 * organiser reviews and publishes, and attendees choose and book in the Connection app.
 * An engine is built for one format; the agent picks one engine per format it runs.
 * Format values are an agent slot's, as the API takes them: 'one_to_one' | 'one_to_many' |
 * 'session_recs'. A matching engine calls session recommendations 'sessions': map between the two
 * with `engineFormatOf` and `agentFormatOf`.
 */
export const CONNECTION_FORMATS = [
  {
    value: 'one_to_one',
    title: 'One to one',
    description: 'Pairs two people: delegates with sponsors, delegates with each other, or both.',
    icon: Agreement01Icon,
  },
  {
    value: 'one_to_many',
    title: 'One to many',
    description: 'Groups delegates into roundtables and hosted sessions, within each capacity.',
    icon: UserGroupIcon,
  },
  {
    value: 'session_recs',
    title: 'Session recommendations',
    description: 'Suggests the programme sessions each delegate should add to their day.',
    icon: PresentationBarChart01Icon,
  },
  {
    value: 'clustering',
    title: 'Clustering',
    description: 'Finds natural groups across the attendee list.',
    icon: UserMultiple02Icon,
    disabled: true,
    disabledReason: 'Coming soon',
  },
];

export const CONNECTION_PAIRINGS = [
  {
    value: 'delegate_sponsor',
    title: 'Delegate and sponsor',
    description: 'Meetings at the sponsor’s stand. The sponsor’s calendar sets the times.',
  },
  {
    value: 'delegate_delegate',
    title: 'Delegate and delegate',
    description: 'Peer introductions, continued by email.',
  },
];

/** An agent slot's format as a matching engine names it: 'session_recs' → 'sessions'. */
export const engineFormatOf = format => (format === 'session_recs' ? 'sessions' : format);

/** A matching engine's format as an agent slot names it: 'sessions' → 'session_recs'. */
export const agentFormatOf = format => (format === 'sessions' ? 'session_recs' : format);

export const getConnectionFormatLabel = value =>
  CONNECTION_FORMATS.find(format => format.value === agentFormatOf(value))?.title || value;

export const getConnectionPairingLabel = value =>
  CONNECTION_PAIRINGS.find(pairing => pairing.value === value)?.title || value;

/**
 * One slot per format the agent runs, and for one to one one per pairing (D2): an agent can run
 * delegate and sponsor meetings and delegate and delegate introductions together. The key names a
 * slot everywhere: `one_to_one:delegate_sponsor`, `one_to_one:delegate_delegate`, `session_recs`.
 */
export const connectionSlotKey = slot =>
  slot?.format === 'one_to_one' ? `one_to_one:${slot.pairing || 'delegate_sponsor'}` : slot?.format;

/** "One to one (delegate and sponsor)", "Session recommendations". */
export const connectionSlotLabel = slot =>
  slot?.format === 'one_to_one'
    ? `${getConnectionFormatLabel(slot.format)} (${getConnectionPairingLabel(slot.pairing || 'delegate_sponsor').toLowerCase()})`
    : getConnectionFormatLabel(slot?.format);

/** Who sends the first request: the other side accepts or passes. */
export const CONNECTION_INITIATORS = [
  {
    value: 'delegate',
    title: 'Delegates pick first',
    description: 'Delegates choose sponsors from their suggestions; sponsors accept or pass.',
  },
  {
    value: 'sponsor',
    title: 'Sponsors pick first',
    description: 'Sponsors choose delegates from their suggestions; delegates accept or pass.',
  },
];

export const CONNECTION_NOTE_OPTIONS = [
  { value: 'off', label: 'No note' },
  { value: 'optional', label: 'Optional note' },
  { value: 'required', label: 'Note required' },
];

export const DEFAULT_CONNECTION_RULES = {
  initiator: 'delegate',
  pickCap: 8,
  // Accepts per responder (the app's responderConfirmCap): how many requests each person on the
  // other side can say yes to. Empty means no limit.
  confirmCap: null,
  note: 'optional',
  showScore: true,
  closesAt: null,
  feedback: true,
};

/**
 * How meetings are laid out on sponsors' calendars, as the Connection app keeps them: the lengths
 * sponsors can offer and the default (`durationOptions`, `defaultDuration`), the gaps between
 * meetings likewise (`bufferOptions`, `defaultBuffer`), and the meeting hours.
 */
export const DEFAULT_CONNECTION_MEETINGS = {
  durationOptions: [15, 20, 30],
  defaultDuration: 20,
  bufferOptions: [5, 10],
  defaultBuffer: 10,
  dayStart: '09:00',
  dayEnd: '17:00',
};

export const CONNECTION_MEETING_DURATIONS = [15, 20, 30, 45];
export const CONNECTION_MEETING_BUFFERS = [0, 5, 10, 15];

/** An agent's meeting layout with today's fields, also from one saved before lengths became lists. */
export const normaliseConnectionMeetings = (meetings = {}) => {
  const defaultDuration = meetings.defaultDuration ?? meetings.duration ?? 20;
  const defaultBuffer = meetings.defaultBuffer ?? meetings.buffer ?? 10;
  return {
    ...DEFAULT_CONNECTION_MEETINGS,
    ...meetings,
    durationOptions: meetings.durationOptions?.length
      ? meetings.durationOptions
      : [defaultDuration],
    defaultDuration,
    bufferOptions: meetings.bufferOptions?.length ? meetings.bufferOptions : [defaultBuffer],
    defaultBuffer,
  };
};

/** The meeting length the events team books by default. */
export const connectionMeetingLength = meetings =>
  normaliseConnectionMeetings(meetings).defaultDuration;

/** Attendee type, read from the imported contact (HubSpot's `attendee_type`, or a CSV column). */
export const getAttendeeRole = metadata => {
  const value = Object.entries(metadata || {})
    .filter(([key]) => /attendee|role|ticket|type/i.test(key))
    .map(([, v]) => String(v || ''))
    .join(' ');
  return /sponsor|exhibitor|partner/i.test(value) ? 'sponsor' : 'delegate';
};

export const CONNECTION_MATCH_STATUS = {
  suggested: { label: 'Suggested', variant: 'secondary' },
  requested: { label: 'Requested', variant: 'sky' },
  accepted: { label: 'Accepted', variant: 'success' },
  declined: { label: 'Declined', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

/** A roundtable invitation (one to many): the delegate hasn't answered, said yes, or said no. */
export const CONNECTION_INVITATION_STATUS = {
  suggested: { label: 'Invited', variant: 'sky' },
  accepted: { label: 'Accepted', variant: 'success' },
  declined: { label: 'Declined', variant: 'outline' },
};

/** The agent's sessions (Agenda): format, whose schedule a session is on, and its tag in the app. */
export const CONNECTION_SESSION_FORMATS = [
  'Keynote',
  'Seminar',
  'Panel',
  'Fireside',
  'Workshop',
  'Roundtable',
  'Networking',
  'Briefing',
];

export const CONNECTION_SESSION_AUDIENCES = [
  { value: 'delegates', label: 'Delegates’ schedule' },
  { value: 'sponsors', label: 'Sponsors’ schedule' },
  { value: 'everyone', label: 'Everyone’s schedule' },
];

export const CONNECTION_SESSION_TAGS = [
  { value: 'programme', label: 'Programme' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'hosted_lunch', label: 'Hosted lunch' },
];

export const CONNECTION_TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Asia/Dubai',
  'Asia/Singapore',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

/**
 * Times on the event's clock. The API, the hubs and the emails all work on the agent's
 * `timezone`, so every Connections screen shows and reads times there, whatever zone the
 * viewer's browser is in. `tz` is the agent's `timezone`; the API's default when it is missing.
 */
export const CONNECTION_DEFAULT_TIMEZONE = 'Europe/London';

const zoned = (iso, tz) => {
  try {
    return dayjs(iso).tz(tz || CONNECTION_DEFAULT_TIMEZONE);
  } catch {
    return dayjs(iso).tz(CONNECTION_DEFAULT_TIMEZONE);
  }
};

/** Format an instant on the event's clock: `eventTime(iso, agent?.timezone, 'HH:mm')`. */
export const eventTime = (iso, tz, pattern = 'ddd D MMM, HH:mm') =>
  iso ? zoned(iso, tz).format(pattern) : '';

const WALL_CLOCK = 'YYYY-MM-DDTHH:mm:ss';

/** An instant as a date-picker value that shows the event's wall-clock time. */
export const toEventPicker = (iso, tz) => (iso ? dayjs(zoned(iso, tz).format(WALL_CLOCK)) : null);

/** A date-picker value read as the event's wall-clock time, returned as an ISO instant. */
export const fromEventPicker = (value, tz) => {
  if (!value || !value.isValid?.()) return null;
  try {
    return dayjs.tz(value.format(WALL_CLOCK), tz || CONNECTION_DEFAULT_TIMEZONE).toISOString();
  } catch {
    return dayjs.tz(value.format(WALL_CLOCK), CONNECTION_DEFAULT_TIMEZONE).toISOString();
  }
};

export const CONNECTION_MEETING_STATUS = {
  requested: { label: 'Waiting for an answer', variant: 'sky' },
  accepted: { label: 'Needs a time', variant: 'warning' },
  // Both said yes and are agreeing a time between themselves (the app's `booking`).
  booking: { label: 'Booking externally', variant: 'sky' },
  booked: { label: 'Booked', variant: 'success' },
  held: { label: 'Held', variant: 'secondary' },
  declined: { label: 'Declined', variant: 'outline' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

/** Who a matching run is for. Selected attendees: a checklist; one to one brings every sponsor in. */
export const CONNECTION_RUN_SCOPES = [
  {
    value: 'new',
    title: 'Only people without suggestions',
    description: 'For late registrants. Everyone else keeps what they have.',
  },
  {
    value: 'selected',
    title: 'Selected attendees',
    description: 'Pick delegates from the list. Every sponsor is matched with them.',
  },
  {
    value: 'all',
    title: 'Everyone',
    description: 'Adds new suggestions for everyone. Nothing published is changed or removed.',
  },
];

/** A run's status: `running`, then `finished` or `failed` (`completed` is the prototype's word for finished). */
export const CONNECTION_RUN_STATUS = {
  running: { label: 'Matching', variant: 'sky' },
  finished: { label: 'Finished', variant: 'success' },
  completed: { label: 'Finished', variant: 'success' },
  failed: { label: 'Didn’t finish', variant: 'destructive' },
};

/** The matching service's stages, as the progress card names them (`runningRun.progress.stage`). */
export const CONNECTION_RUN_STAGES = {
  queued: 'Waiting to start',
  fetching: 'Reading the attendees',
  preparing: 'Preparing the attendees',
  scoring: 'Scoring each pair',
  solving: 'Choosing the best matches',
  writing: 'Saving the matches',
};

/** "Matching didn't finish: {reason}". */
export const connectionRunFailedText = run =>
  run?.failedReason ? `Matching didn't finish: ${run.failedReason}` : "Matching didn't finish.";

/** When in the event a delivery goes. Today's schedule is on the day only; HubSpot before the event only. */
export const CONNECTION_DELIVERY_TIMINGS = [
  {
    value: 'pre_event',
    label: 'Before the event',
    description: 'Before the doors open: get people into the app and choosing.',
  },
  {
    value: 'on_day',
    label: 'On the day',
    description: 'At the event: today’s meetings, rooms and stands.',
  },
];

/** What a delivery is for. Link purposes send each person their own Connection app link. */
export const CONNECTION_DELIVERY_PURPOSES = [
  {
    value: 'invite',
    title: 'Invite to the app',
    description: 'Each person’s link to their meetings hub, the first time they get it.',
    defaultAudience: 'not_received',
    carriesLink: true,
    subject: '{first_name}, choose who to meet at {event_name}',
    message:
      'Your meetings hub is ready. We’ve suggested people worth meeting at {event_name}: open your link, choose who you want to meet and pick a time.',
    pushTitle: 'Your meetings are ready',
  },
  {
    value: 'reminder',
    title: 'Reminder',
    description: 'A nudge for anyone who hasn’t opened their link or picked yet.',
    defaultAudience: 'not_signed_in',
    carriesLink: true,
    subject: 'Your meetings at {event_name} are waiting',
    message:
      'You haven’t chosen who to meet yet. Your suggestions are waiting in your meetings hub: it takes two minutes.',
    pushTitle: 'Your matches are waiting',
  },
  {
    value: 'results',
    title: 'New suggestions',
    description: 'After a new run is published: there are more people to meet.',
    defaultAudience: 'everyone',
    carriesLink: true,
    subject: 'New people to meet at {event_name}',
    message: 'We’ve added new suggestions to your meetings hub. Have a look before they fill up.',
    pushTitle: 'New people to meet',
  },
  {
    value: 'schedule',
    title: 'Today’s schedule',
    description: 'On the day: each person’s meetings, times and places.',
    defaultAudience: 'with_meetings',
    carriesLink: true,
    timings: ['on_day'],
    subject: 'Your meetings today at {event_name}',
    message: 'Here’s your day: {meetings_today} meetings, the first at {first_meeting}.',
    pushTitle: 'Your day at {event_name}',
  },
  {
    value: 'announcement',
    title: 'Announcement',
    description: 'A short update to everyone or a group, such as a room change. No personal link.',
    defaultAudience: 'everyone',
    carriesLink: false,
    subject: 'An update from {event_name}',
    message: '',
    pushTitle: '',
  },
  {
    value: 'pending_actions',
    title: 'Pending actions email',
    description: 'One email to everybody with something waiting in their hub.',
    internal: true,
  },
];

/** Who gets a delivery. The activity groups are the live app's broadcast segments. */
export const CONNECTION_DELIVERY_AUDIENCE_GROUPS = [
  { value: 'groups', label: 'Groups' },
  { value: 'link', label: 'Their link' },
  { value: 'activity', label: 'What they’ve done so far' },
  { value: 'pick', label: 'One by one' },
];

export const CONNECTION_DELIVERY_AUDIENCES = [
  { value: 'everyone', label: 'Everyone', group: 'groups' },
  { value: 'delegates', label: 'Delegates', group: 'groups' },
  { value: 'sponsors', label: 'Sponsors', group: 'groups' },
  { value: 'not_received', label: 'Haven’t had their link yet', group: 'link' },
  { value: 'not_signed_in', label: 'Haven’t opened their link', group: 'link' },
  { value: 'sponsors_no_times', label: 'Sponsors without times', group: 'link' },
  { value: 'with_meetings', label: 'Have a meeting booked', group: 'activity' },
  { value: 'no_meetings', label: 'No meetings booked yet', group: 'activity' },
  { value: 'awaiting_reply', label: 'Have a request waiting for their reply', group: 'activity' },
  {
    value: 'needs_time',
    label: 'Have a yes but still need to pick a time',
    group: 'activity',
  },
  { value: 'pick', label: 'Pick people', group: 'pick' },
  { value: 'pending_actions', label: 'Have something waiting', internal: true },
];

/** Where a tap on the link (the email's button, the notification, WhatsApp's button) opens their hub. */
export const CONNECTION_DELIVERY_OPENS = [
  { value: 'home', label: 'Their home screen' },
  { value: 'meetings', label: 'Their meetings (the schedule, for delegates)' },
  { value: 'venue', label: 'The venue map' },
];

/**
 * Every Connections email is sent from Bridged's verified events address, with the organiser's from
 * name and reply-to. Custom sender domains are not offered yet.
 */
export const CONNECTION_EMAIL_FROM_ADDRESS = 'no-reply@mail.bridged.events';

/** "Northway Roads Expo <no-reply@mail.bridged.events>". */
export const connectionEmailSender = fromName =>
  fromName ? `${fromName} <${CONNECTION_EMAIL_FROM_ADDRESS}>` : CONNECTION_EMAIL_FROM_ADDRESS;

/** "a, b and c", or "a, b, c and 5 more" when there are more than `shown`. */
export const formatAddressList = (addresses = [], shown = 3) => {
  const list = [...new Set((addresses || []).filter(Boolean))];
  if (list.length <= 1) return list[0] || '';
  if (list.length <= shown) return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return `${list.slice(0, shown).join(', ')} and ${list.length - shown} more`;
};

export const CONNECTION_DELIVERY_CHANNELS = [
  {
    value: 'email',
    title: 'Email',
    description: `From ${CONNECTION_EMAIL_FROM_ADDRESS} with the event’s name, with a button to each person’s link.`,
    icon: Mail01Icon,
  },
  {
    value: 'whatsapp',
    title: 'WhatsApp',
    description: 'An approved template, to anyone with a mobile number.',
    icon: WhatsappIcon,
  },
  {
    value: 'push',
    title: 'App notification',
    description: 'To people signed in to the Connection app who allowed notifications.',
    icon: Notification01Icon,
  },
  {
    value: 'hubspot',
    title: 'HubSpot',
    description: 'Writes each person’s link to their HubSpot contact. Sends nothing.',
    icon: RefreshIcon,
    linkOnly: true,
    purposes: ['invite', 'reminder', 'results'],
    timings: ['pre_event'],
  },
  {
    value: 'qr',
    title: 'QR code on badge',
    description: 'A QR per person for the badge printer. Scanning it opens their own hub.',
    icon: QrCodeIcon,
    linkOnly: true,
    purposes: ['invite', 'schedule'],
  },
];

/** Why a channel can't be used for a delivery's purpose and timing, or null when it can. */
export const connectionChannelBlockedReason = (channel, purpose, timing) => {
  const option = CONNECTION_DELIVERY_CHANNELS.find(item => item.value === channel);
  if (!option) return null;
  if (option.purposes && !option.purposes.includes(purpose)) {
    return purpose === 'announcement'
      ? 'Announcements have no personal link to write.'
      : 'Not used for this kind of delivery.';
  }
  if (option.timings && !option.timings.includes(timing)) return 'Before the event only.';
  return null;
};

export const getConnectionDeliveryChannelLabel = value =>
  CONNECTION_DELIVERY_CHANNELS.find(channel => channel.value === value)?.title || value;

export const CONNECTION_DELIVERY_STATUS = {
  draft: { label: 'Draft', variant: 'secondary' },
  scheduled: { label: 'Scheduled', variant: 'sky' },
  sending: { label: 'Sending', variant: 'sky' },
  sent: { label: 'Sent', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
};

// In the order the round is run: set up (People), match, meet, deliver, hear back, then report.
// Agenda shows only for formats that use the programme (see connectionOverviewTabs).
export const CONNECTION_OVERVIEW_TABS = [
  { value: 'overview', label: 'Overview', icon: DashboardSquare01Icon },
  { value: 'people', label: 'People', icon: UserMultiple02Icon },
  { value: 'matches', label: 'Matches', icon: Agreement01Icon },
  { value: 'meetings', label: 'Meetings', icon: Calendar03Icon },
  { value: 'agenda', label: 'Agenda', icon: PresentationBarChart01Icon, programme: true },
  { value: 'deliveries', label: 'Deliveries', icon: Mail01Icon },
  { value: 'feedback', label: 'Feedback', icon: ThumbsUpIcon },
  { value: 'report', label: 'Report', icon: Analytics01Icon },
  { value: 'settings', label: 'Settings', icon: Settings02Icon },
];

/** Formats that put sessions in people's schedules, so the agent has an agenda. */
const AGENDA_FORMATS = ['one_to_many', 'session_recs', 'sessions'];

/** The overview's tabs for an agent: Agenda only when one of its formats uses the programme. */
export const connectionOverviewTabs = agent =>
  CONNECTION_OVERVIEW_TABS.filter(
    tab =>
      !tab.programme ||
      (agent?.formats || []).some(slot => AGENDA_FORMATS.includes(agentFormatOf(slot?.format)))
  );
